import { mapActions, mapGetters } from "vuex";
import { cleanObject, formatDate } from "./utils/utils";
import toast from "@/utils/toast";

export default {
  name: "IdpCourseCompletionReportMain",

  components: {},
  data() {
    return {
      filters: {
        page: 1,
        pageSize: 10,
        order: "desc",
        field: "updated_at",
        search: "",
        learning_type: "social_learning",
        type: "completion",
        idp_period_id: null,
        all: false,
        nik: null,
        status: null,
        training_scope: null,
        training_category: null,
        platform: null,
        training_method_id: null,
        training_type: null,
        competency_id: null,
        coaching_type_id: null,
      },
      activeTab: "social_learning",
      position: null,
      isLoading: false,
      isLoadingEmployeeList: false,
      isModalLoading: false,
      isLoadingSummary: false,
      isUpdateSummaryLoading: false,
      summaryData: null,
      _searchTimer: null,
      _employeeSearchTimer: null,
      _employeeListSearchTimer: null,
      employeeListSearch: "",
      employeeListPage: 1,
      employeeListPageSize: 10,
      showModal: false,
      modalData: null,
      idpPeriodOptions: [],
      activityData: null,
      isSubmitFeedbackLoading: false,
      activeFiltersCount: 0,
      isLoadingApprove: false,
      showConfirmationSubmitModal: false,
      isSubmitting: false,
    };
  },

  computed: {
    ...mapGetters("courseCompletion", [
      "idpLearningReport",
      "summaryIdpLearningReport",
      "trainingMethods",
      "trainingTypes",
      "competencies",
      "subordinates",
      "subordinatesMeta",
    ]),

    showingFrom() {
      if (!this.idpLearningReport.totalItems) return 0;
      return (this.filters.page - 1) * this.filters.pageSize + 1;
    },

    showingTo() {
      const to = this.filters.page * this.filters.pageSize;
      return to > this.idpLearningReport.totalItems ? this.idpLearningReport.totalItems : to;
    },

    selectedSubordinate() {
      return this.subordinates.find((sub) => String(sub.nik) === String(this.filters.nik)) || null;
    },

    selectedPeriodOption: {
      get() {
        return this.idpPeriodOptions.find((option) => String(option.value) === String(this.filters.idp_period_id)) || null;
      },
      set(option) {
        this.filters.idp_period_id = option?.value || null;
      },
    },

    selectedEmployeeName() {
      return this.selectedSubordinate?.name || "-";
    },

    pendingApprovalCount() {
      return this.showEmployeeList ? this.employeeListTotalSubmissions : this.idpLearningReport?.totalItems || 0;
    },

    showEmployeeList() {
      return !this.filters.nik;
    },

    employeeListTotalSubmissions() {
      return this.subordinates.reduce((total, item) => total + Number(item.total_learning || 0), 0);
    },

    employeeListTotalRecords() {
      return this.subordinatesMeta?.totalItems || this.subordinates.length || 0;
    },

    employeeListShowingFrom() {
      if (!this.employeeListTotalRecords) return 0;
      return (this.employeeListPage - 1) * this.employeeListPageSize + 1;
    },

    employeeListShowingTo() {
      const to = this.employeeListPage * this.employeeListPageSize;
      return to > this.employeeListTotalRecords ? this.employeeListTotalRecords : to;
    },

    statusFilterOptions() {
      const optionMap = {
        social_learning: [
          { value: null, text: "All Status" },
          { value: "approved", text: "Approved" },
          { value: "scheduled", text: "Scheduled" },
          { value: "in_progress", text: "In Progress" },
          { value: "in_review", text: "In Review" },
          { value: "completed", text: "Completed" },
        ],
        experiential_learning: [
          { value: null, text: "All Status" },
          { value: "approved", text: "Approved" },
          { value: "todo", text: "To Do" },
          { value: "in_progress", text: "In Progress" },
          { value: "in_review", text: "In Review" },
          { value: "completed", text: "Completed" },
        ],
      };

      return optionMap[this.filters.learning_type] || optionMap.social_learning;
    },
  },

  watch: {
    filters: {
      deep: true,
      handler(newFilters) {
        this.updateUrlQuery(newFilters);
      },
    },

    async "filters.idp_period_id"() {
      this.filters.page = 1;

      await this.loadApprovalSubordinates(this.employeeListSearch, false);

      if (!this.filters.nik) {
        this.resetIdpLearnings();
        return;
      }

      this.loadSummaryLearning({ nik: this.filters.nik, idp_period_id: this.filters.idp_period_id });

      this.loadData();
    },

    "filters.nik"(newVal) {
      this.filters.page = 1;

      this.position = this.selectedSubordinate?.position || null;

      if (!newVal) {
        this.resetIdpLearnings();
        return;
      }

      this.loadData();

      this.loadSummaryLearning({ nik: newVal });
    },

    "$route.query": {
      handler() {
        this.syncFiltersFromQuery();
      },
      immediate: true,
    },
  },

  methods: {
    ...mapActions("courseCompletion", [
      "getIdpLearningReport",
      "getSocialLearningDetail",
      "uploadCertificateLearning",
      "updateCoachingSummary",
      "getExperientialLearningDetail",
      "submitFeedbackProjectExperientialLearning",
      "addCoachingSchedules",
      "getSummaryIdpLearningReport",
      "submitFeedbackSummary",
      "completeSocialLearning",
      "getIdpPeriod",
      "getTrainingMethods",
      "getTrainingTypes",
      "getCompetencies",
      "resetIdpLearnings",
      "getSubordinates",
    ]),

    showFilterModal() {
      this.$bvModal.show("modal-filter");
    },

    getOptionLabel(options = [], value) {
      const option = options.find((item) => String(item.value) === String(value));
      return option?.label || option?.text || value || "-";
    },

    async showUpdateSummaryModal(data) {
      this.summaryData = data;
      this.$bvModal.show("update-summary-modal");
    },

    async showUpdateActivityModal(data) {
      this.activityData = data;

      this.$bvModal.show("update-activity-modal");
    },

    buildQueryFromFilters() {
      const raw = {
        page: this.filters.page,
        pageSize: this.filters.pageSize,
        order: this.filters.order,
        field: this.filters.field,
        search: this.filters.search,
        all: this.filters.all,
        learning_type: this.filters.learning_type,
        idp_period_id: this.filters.idp_period_id,
        status: this.filters.status,
        training_scope: this.filters.training_scope,
        training_category: this.filters.training_category,
        platform: this.filters.platform,
        training_method_id: this.filters.training_method_id,
        training_type: this.filters.training_type,
        competency_id: this.filters.competency_id,
        nik: this.filters.nik,
        coaching_type_id: this.filters.coaching_type_id,
        type: this.filters.type,
        reviewDetail: true,
      };

      return cleanObject(raw);
    },

    async loadData() {
      if (!this.filters.nik) {
        this.resetIdpLearnings();
        return;
      }

      try {
        this.isLoading = true;

        const query = this.buildQueryFromFilters();

        await this.getIdpLearningReport(query);

        if (this.idpLearningReport.page) this.filters.page = this.idpLearningReport.page;
        if (this.idpLearningReport.limit) this.filters.pageSize = this.idpLearningReport.limit;
      } finally {
        this.isLoading = false;
      }
    },

    async onTabChange(tab) {
      const isEmployeeDetail = Boolean(this.filters.nik);

      this.activeTab = tab;

      this.filters.learning_type = tab;
      this.modalData = null;
      this.filters.page = 1;
      this.resetFilters(false);

      if (isEmployeeDetail) {
        this.loadSummaryLearning({ tab });
        this.loadData();
        return;
      }

      await this.loadApprovalSubordinates(this.employeeListSearch, false);
      this.position = null;
      this.resetIdpLearnings();
    },

    handleComplete() {
      this.showConfirmationSubmitModal = true;
    },

    onPageChange(payload) {
      let page = null;
      if (typeof payload === "number") page = payload;
      else if (payload?.currentPage !== undefined) page = payload.currentPage;
      else if (payload?.currentPageNumber !== undefined) page = payload.currentPageNumber;
      if (page === null) return;
      this.filters.page = page;
      this.loadData();
    },

    onPageChangeFromBv(page) {
      this.filters.page = page;
      this.loadData();
    },

    onPerPageChange(payload) {
      let per = null;
      if (typeof payload === "number") per = payload;
      else if (payload?.currentPerPage !== undefined) per = payload.currentPerPage;
      else if (payload?.perPage !== undefined) per = payload.perPage;
      if (per === null) return;
      this.filters.pageSize = per;
      this.filters.page = 1;
      this.loadData();
    },

    onSortChange(params) {
      if (!Array.isArray(params) || params.length === 0) return;
      const { field, type } = params[0] || {};
      const isReset = type === "none";
      this.filters = {
        ...this.filters,
        field: isReset ? null : field,
        order: isReset ? null : type || "asc",
        page: 1,
      };
      this.loadData();
    },

    onSearch() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.filters.page = 1;
        this.loadData();
      }, 500);
    },

    clearFilter() {
      this.filters = { ...this.filters, search: "", page: 1 };
      this.loadData();
    },

    updateUrlQuery(filters) {
      const query = cleanObject({
        ...this.$route.query,
        page: filters.page,
        pageSize: filters.pageSize,
        order: filters.order,
        field: filters.field,
        search: filters.search,
        nik: filters.nik,
        idp_period_id: filters.idp_period_id,
        learning_type: filters.learning_type,
        status: filters.status,
      });

      const current = JSON.stringify(this.$route.query);
      const next = JSON.stringify(query);

      if (current !== next) {
        this.$router.replace({ query }).catch(() => {});
      }
    },

    syncFiltersFromQuery() {
      const query = this.$route.query;
      const parsed = {
        page: Number(query.page) || 1,
        pageSize: Number(query.pageSize) || 10,
        order: query.order || null,
        field: query.field || null,
        search: query.search || "",
        nik: query.nik || this.filters.nik,
        idp_period_id: query.idp_period_id ? Number(query.idp_period_id) : this.filters.idp_period_id,
        learning_type: query.learning_type || this.filters.learning_type,
        status: query.status || this.filters.status,
      };

      Object.keys(parsed).forEach((key) => {
        this.$set(this.filters, key, parsed[key]);
      });
    },

    async handleViewDetail(row) {
      this.isModalLoading = true;
      this.selectedId = row.id;
      this.showModal = true;

      switch (this.filters.learning_type) {
        case "social_learning":
          {
            const detail = await this.getSocialLearningDetail({ id: row.id, query: { nik: this.filters.nik } });
            this.modalData = { ...detail, is_actionable: row?.is_actionable };
          }
          break;
        case "experiential_learning":
          {
            const detail = await this.getExperientialLearningDetail({ id: row.id, query: { nik: this.filters.nik } });
            this.modalData = { ...detail, is_actionable: row?.is_actionable };
          }
          break;
        default:
          this.modalData = null;
      }

      this.$nextTick(() => {
        if (this.filters.learning_type === "social_learning") this.$bvModal.show("social-learning-modal");

        if (this.filters.learning_type === "experiential_learning") this.$bvModal.show("experiential-learning-modal");
      });
      this.isModalLoading = false;
    },

    async loadSummaryLearning({ nik, tab, idp_period_id }) {
      const selectedNik = nik || this.filters.nik;

      if (!selectedNik) {
        this.resetIdpLearnings();
        return;
      }

      this.isLoadingSummary = true;

      const payload = {
        learning_type: tab || this.filters.learning_type,
        nik: selectedNik,
        idp_period_id: idp_period_id || this.filters.idp_period_id,
        type: "completion",
      };

      await this.getSummaryIdpLearningReport(payload);

      this.isLoadingSummary = false;
    },

    async loadIdpPeriod() {
      const idpPeriod = await this.getIdpPeriod();
      const now = new Date();

      this.filters.idp_period_id =
        idpPeriod.find((p) => new Date(p.start_date) <= now && new Date(p.end_date) >= now)?.id || null;

      this.idpPeriodOptions = idpPeriod.map((p) => ({
        text: this.formatPeriodLabel(p.start_date, p.end_date),
        value: p.id,
      }));
    },

    formatPeriodLabel(startDate, endDate) {
      if (!startDate || !endDate) return "";

      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    },

    async submitFeedback(payload) {
      this.$swal({
        title: "Apakah Anda yakin?",
        text: "Apakah Anda yakin ingin mengirim umpan balik?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#00D97E",
        cancelButtonColor: "#E63757",
        confirmButtonText: "Ya, kirim",
        cancelButtonText: "Batal",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            this.isSubmitFeedbackLoading = true;

            await this.submitFeedbackProjectExperientialLearning({ id: payload.id, data: { feedback: payload.feedback } });

            toast({ type: "success", title: "Success", message: "Umpan balik berhasil dikirim" });

            this.$bvModal.hide("update-activity-modal");
          } catch (error) {
            console.error("Update error:", error);
            const msg = error?.response?.data?.error || error?.message || "Terjadi kesalahan.";

            toast({ type: "error", title: "Error", message: msg });
          } finally {
            this.isSubmitFeedbackLoading = false;
            const detail = await this.getExperientialLearningDetail({
              id: this.modalData.id,
              query: { nik: this.filters.nik },
            });
            this.modalData = detail;

            this.loadSummaryLearning({
              nik: this.filters.nik,
              tab: this.filters.learning_type,
              idp_period_id: this.filters.idp_period_id,
            });
            this.loadData();
          }
        }
      });
    },

    applyFilters(appliedFilters) {
      const cleaned = cleanObject(appliedFilters);

      Object.entries(cleaned).forEach(([key, val]) => {
        this.$set(this.filters, key, val);
      });

      this.$set(this.filters, "page", 1);
      this.updateActiveFilterCount();
      this.loadData();

      this.$bvModal.hide("modal-filter");
    },

    async completeCoaching(id) {
      if (!id) return;

      try {
        await this.completeSocialLearning({ id });

        toast({ type: "success", title: "Success", message: "Pembelajaran berhasil diselesaikan" });
        this.showConfirmationSubmitModal = false;
        this.$bvModal.hide("social-learning-modal");
      } catch (error) {
        const msg = error?.response?.data?.error || error?.message || "Terjadi kesalahan.";
        toast({ type: "error", title: "Error", message: msg });
      } finally {
        this.loadData();
      }
    },

    async updateSummary(payload) {
      this.$swal({
        title: "Apakah Anda yakin?",
        text: "Apakah Anda yakin ingin mengirim ringkasan umpan balik?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#00D97E",
        cancelButtonColor: "#E63757",
        confirmButtonText: "Ya, kirim",
        cancelButtonText: "Batal",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            this.isUpdateSummaryLoading = true;

            await this.submitFeedbackSummary({ id: payload.id, data: { feedback: payload.feedback } });

            toast({ type: "success", title: "Success", message: "Umpan balik berhasil dikirim" });
          } catch (error) {
            console.error("Update error:", error);
            const msg = error?.response?.data?.error || error?.message || "Terjadi kesalahan.";

            toast({ type: "error", title: "Error", message: msg });
          } finally {
            this.isUpdateSummaryLoading = false;
            this.$bvModal.hide("update-summary-modal");
            this.$bvModal.hide("social-learning-modal");
            this.loadData();
          }
        }
      });
    },

    updateActiveFilterCount() {
      const { status, training_scope, training_category, platform, training_method_id, training_type, competency_id, coaching_type_id } = this.filters;
      const vals = [status, training_scope, training_category, platform, training_method_id, training_type, competency_id, coaching_type_id];

      this.activeFiltersCount = vals.filter((x) => x !== null && x !== undefined && x !== "").length;
    },

    resetFilters(shouldLoad = true) {
      const reset = {
        status: null,
        training_scope: null,
        training_category: null,
        platform: null,
        training_method_id: null,
        training_type: null,
        competency_id: null,
        coaching_type_id: null,
        page: 1,
      };

      Object.entries(reset).forEach(([key, val]) => {
        this.$set(this.filters, key, val);
      });

      this.activeFiltersCount = 0;

      if (shouldLoad) this.loadData();
    },

    clearSingleFilter(filterKey) {
      if (this.filters.hasOwnProperty(filterKey)) {
        this.$set(this.filters, filterKey, null);
        this.filters.page = 1;
        this.updateActiveFilterCount();
        this.loadData();
      }
    },

    onStatusFilterChange(status) {
      this.filters.status = status || null;
      this.filters.page = 1;
      this.updateActiveFilterCount();
      this.loadData();
    },

    buildSubordinateQuery(search = "", approvalOnly = false) {
      return cleanObject({
        page: this.employeeListPage,
        pageSize: this.employeeListPageSize,
        all: false,
        field: "fullname",
        order: "asc",
        search,
        bibOnly: true,
        approvalOnly,
        countApprovalOnly: true,
        approvalType: "completion",
        learning_type: this.filters.learning_type,
        idp_period_id: this.filters.idp_period_id,
      });
    },

    async loadApprovalSubordinates(search = "", approvalOnly = false) {
      this.isLoadingEmployeeList = true;
      try {
        await this.getSubordinates(this.buildSubordinateQuery(search, approvalOnly));
        this.employeeListPage = this.subordinatesMeta?.page || this.employeeListPage;
        this.employeeListPageSize = this.subordinatesMeta?.limit || this.employeeListPageSize;
      } finally {
        this.isLoadingEmployeeList = false;
      }

      if (this.filters.nik && !this.selectedSubordinate && search === "") {
        const currentPage = this.employeeListPage;
        this.employeeListPage = 1;
        await this.getSubordinates(this.buildSubordinateQuery(this.filters.nik));
        this.employeeListPage = currentPage;
        if (!this.selectedSubordinate) this.filters.nik = null;
      }
    },

    selectApprovalEmployee(row) {
      if (!row?.nik) return;
      this.filters.nik = row.nik;
      this.position = row.position || null;
    },

    backToEmployeeList() {
      this.filters.nik = null;
      this.position = null;
      this.resetIdpLearnings();
    },

    onEmployeeListSearch() {
      clearTimeout(this._employeeListSearchTimer);
      this._employeeListSearchTimer = setTimeout(() => {
        this.employeeListPage = 1;
        this.loadApprovalSubordinates(this.employeeListSearch, false);
      }, 350);
    },

    onEmployeeListPageChange(page) {
      this.employeeListPage = page;
      this.loadApprovalSubordinates(this.employeeListSearch, false);
    },

    onEmployeeListPerPageChange(pageSize) {
      this.employeeListPageSize = pageSize;
      this.employeeListPage = 1;
      this.loadApprovalSubordinates(this.employeeListSearch, false);
    },

    onEmployeeSearch(search, loading) {
      clearTimeout(this._employeeSearchTimer);
      const term = String(search || "").trim();

      if (term && term.length < 2) return;

      loading?.(true);
      this._employeeSearchTimer = setTimeout(async () => {
        try {
          await this.loadApprovalSubordinates(term, false);
        } finally {
          loading?.(false);
        }
      }, 350);
    },
  },

  async mounted() {
    this.syncFiltersFromQuery();

    await Promise.all([
      this.loadIdpPeriod(),
      this.getTrainingMethods(),
      this.getTrainingTypes(),
      this.getCompetencies(),
    ]);

    await this.loadApprovalSubordinates("", false);
  },

  beforeDestroy() {
    clearTimeout(this._searchTimer);
    clearTimeout(this._employeeSearchTimer);
    clearTimeout(this._employeeListSearchTimer);
    this.resetIdpLearnings();
  },
};
